<template>
  <main class="public-recipe-view">
    <section class="public-recipe-view__inner">
      <LoaderView v-if="loading" text="Chargement de la recette..." />

      <article v-else-if="recipe" class="recipe-layout">
        <header class="recipe-hero">
          <div class="recipe-hero__media">
            <img v-if="recipe.photo" :src="mediaSrc(recipe.photo)" :alt="recipe.name" class="recipe-hero__image" />
            <div v-else class="recipe-hero__fallback">
              {{ recipe.name }}
            </div>
          </div>

          <div class="recipe-hero__content">
            <p class="recipe-hero__eyebrow">Recette Mes Meilleurs Menus</p>
            <h1 class="recipe-hero__title">{{ recipe.name }}</h1>

            <div class="recipe-hero__meta">
              <span class="recipe-chip">{{ recipe.dish_type_display || recipe.dish_type }}</span>
            </div>

            <div v-if="highlightFlags.length" class="recipe-hero__flags">
              <span v-for="flag in highlightFlags" :key="flag" class="recipe-flag">{{ flag }}</span>
            </div>

            <section class="recipe-info" aria-label="Informations clés de la recette">
              <article v-for="card in recipeInfoCards" :key="card.label" class="recipe-info-card">
                <div class="recipe-info-card__icon">
                  <font-awesome-icon :icon="card.icon" />
                </div>
                <div class="recipe-info-card__body">
                  <p class="recipe-info-card__label">{{ card.label }}</p>
                  <p class="recipe-info-card__value">{{ card.value }}</p>
                </div>
              </article>
            </section>

            <div class="recipe-tools">
              <div class="recipe-qr" aria-label="QR code de la recette">
                <p class="recipe-qr__label">QR code recette</p>
                <img v-if="qrCodeDataUrl" :src="qrCodeDataUrl" :alt="`QR code vers ${recipe.name}`"
                  class="recipe-qr__image" />
                <div v-else class="recipe-qr__placeholder">
                  <font-awesome-icon :icon="['fas', 'qrcode']" />
                </div>
              </div>

              <div class="recipe-actions">
                <div class="recipe-action-group">
                  <p class="recipe-action-group__label">Partager :</p>

                  <div class="recipe-action-group__buttons">
                    <a :href="facebookShareUrl" target="_blank" rel="noopener noreferrer"
                      class="recipe-action-icon recipe-action-icon--facebook" aria-label="Partager sur Facebook"
                      title="Partager sur Facebook">
                      <font-awesome-icon :icon="['fab', 'facebook-f']" />
                    </a>

                    <a :href="linkedinShareUrl" target="_blank" rel="noopener noreferrer"
                      class="recipe-action-icon recipe-action-icon--linkedin" aria-label="Partager sur LinkedIn"
                      title="Partager sur LinkedIn">
                      <font-awesome-icon :icon="['fab', 'linkedin-in']" />
                    </a>

                    <button type="button" class="recipe-action-icon recipe-action-icon--instagram"
                      aria-label="Partager sur Instagram" title="Partager sur Instagram" @click="shareOnInstagram">
                      <font-awesome-icon :icon="['fab', 'instagram']" />
                    </button>
                  </div>
                </div>

                <div class="recipe-action-group">
                  <p class="recipe-action-group__label">Téléchargement et Impression :</p>

                  <div class="recipe-action-group__buttons">
                    <button type="button" class="recipe-action-icon recipe-action-icon--utility"
                      aria-label="Imprimer la recette au format A4" title="Imprimer la recette au format A4"
                      @click="printRecipe">
                      <font-awesome-icon :icon="['fas', 'print']" />
                    </button>

                    <button type="button" class="recipe-action-icon recipe-action-icon--utility"
                      :disabled="downloadingPdf"
                      :aria-label="downloadingPdf ? 'Téléchargement du PDF en cours' : 'Télécharger le PDF recette'"
                      :title="downloadingPdf ? 'Téléchargement du PDF en cours' : 'Télécharger le PDF recette'"
                      @click="downloadPdf">
                      <font-awesome-icon :icon="['fas', 'file-arrow-down']" />
                    </button>
                  </div>
                </div>

                <p v-if="actionMessage" class="recipe-actions__feedback">{{ actionMessage }}</p>
              </div>
            </div>
          </div>
        </header>

        <section class="recipe-content" aria-labelledby="ingredients-title">
          <div class="recipe-content__main">
            <section class="recipe-panel">
              <h2 id="ingredients-title">Ingrédients</h2>
              <div class="ingredients-tabs" role="tablist" aria-label="Nombre de personnes">
                <button v-for="option in ingredientServingsOptions" :key="option.value" type="button"
                  class="ingredients-tab" :class="{ 'is-active': activeIngredientServings === option.value }"
                  :aria-selected="activeIngredientServings === option.value ? 'true' : 'false'"
                  @click="activeIngredientServings = option.value">
                  {{ option.label }}
                </button>
              </div>
              <ul class="ingredients-list">
                <li v-for="(ingredient, index) in visibleDishLines" :key="index">
                  <span>{{ ingredientName(ingredient) }}</span>
                  <strong>{{ formattedIngredientAmount(ingredient) }}</strong>
                </li>
              </ul>
            </section>

            <section class="recipe-panel" aria-labelledby="preparation-title">
              <h2 id="preparation-title">Préparation</h2>
              <div class="recipe-text">
                {{ recipe.recipe }}
              </div>
            </section>
          </div>
        </section>

        <p class="recipe-promo-text">
          Recette proposée par le programme personnalisé Mes Meilleurs Menus pour mieux manger pour la santé et le
          climat. C’est 100% gratuit : retrouvez toutes les recettes sur la web application Mes Meilleurs Menus sur
          www.mmmenus.fr
        </p>

        <section v-for="section in relatedSections" :key="section.key" class="related-section"
          :aria-labelledby="`${section.key}-title`">
          <div class="related-section__header">
            <h2 :id="`${section.key}-title`">{{ section.title }}</h2>
          </div>

          <div class="related-grid">
            <router-link v-for="relatedRecipe in section.recipes" :key="relatedRecipe.id" class="related-card"
              :to="{ name: 'recipe', params: { id: relatedRecipe.id } }">
              <div class="related-card__media">
                <img v-if="relatedRecipe.photo" :src="mediaSrc(relatedRecipe.photo)" :alt="relatedRecipe.name"
                  class="related-card__image" />
                <div v-else class="related-card__fallback">
                  {{ relatedRecipe.name }}
                </div>
              </div>

              <div class="related-card__content">
                <p class="related-card__meta">{{ relatedRecipe.preparation_time }} min</p>
                <h3 class="related-card__title">{{ relatedRecipe.name }}</h3>
                <p class="related-card__excerpt">{{ shortRecipe(relatedRecipe.recipe) }}</p>
              </div>
            </router-link>
          </div>
        </section>
      </article>
    </section>

    <section v-if="recipe" class="recipe-export-print" aria-hidden="true">
      <article ref="pdfSheet" class="recipe-export-sheet">
        <header class="recipe-export-sheet__hero">
          <div class="recipe-export-sheet__brand">
            <img :src="logoSrc" alt="Mes Meilleurs Menus" class="recipe-export-sheet__logo" />
            <p class="recipe-export-sheet__eyebrow">Recette Mes Meilleurs Menus</p>
          </div>

          <div class="recipe-export-sheet__hero-main">
            <div class="recipe-export-sheet__hero-copy">
              <p class="recipe-export-sheet__type">{{ recipe.dish_type_display || recipe.dish_type }}</p>
              <h1 class="recipe-export-sheet__title">{{ recipe.name }}</h1>
            </div>

            <div class="recipe-export-sheet__qr">
              <img v-if="qrCodeDataUrl" :src="qrCodeDataUrl" :alt="`QR code vers ${recipe.name}`"
                class="recipe-export-sheet__qr-image" />
            </div>
          </div>
        </header>

        <section class="recipe-export-sheet__summary">
          <div v-if="recipe.photo" class="recipe-export-sheet__photo-wrap">
            <img :src="mediaSrc(recipe.photo)" :alt="recipe.name" class="recipe-export-sheet__photo" />
          </div>

          <div class="recipe-export-sheet__meta">
            <article v-for="card in recipeInfoCards" :key="`${card.label}-print`" class="recipe-export-meta">
              <div class="recipe-export-meta__icon">
                <font-awesome-icon :icon="card.icon" />
              </div>
              <div>
                <p class="recipe-export-meta__label">{{ card.label }}</p>
                <p class="recipe-export-meta__value">{{ card.value }}</p>
              </div>
            </article>
          </div>
        </section>

        <section class="recipe-export-sheet__columns">
          <section class="recipe-export-panel">
            <h2>Ingrédients</h2>
            <ul class="recipe-export-ingredients">
              <li v-for="(ingredient, index) in visibleDishLines" :key="`print-${index}`">
                <span>{{ ingredientName(ingredient) }}</span>
                <strong>{{ ingredient.quantity }} {{ ingredient.unit }}</strong>
              </li>
            </ul>
          </section>

          <section class="recipe-export-panel recipe-export-panel--preparation">
            <h2>Préparation</h2>
            <div class="recipe-export-text">{{ recipe.recipe }}</div>
          </section>
        </section>

        <p class="recipe-export-sheet__footer">
          Recette proposée par le programme personnalisé Mes Meilleurs Menus pour mieux manger pour la santé et le
          climat. C’est 100% gratuit : retrouvez toutes les recettes sur la web application Mes Meilleurs Menus sur
          www.mmmenus.fr
        </p>
      </article>
    </section>
  </main>
</template>

<script>
import html2pdf from "html2pdf.js";
import QRCode from "qrcode";

import { api } from "@/api";
import LoaderView from "@/components/LoaderView.vue";

const HIDDEN_INGREDIENT_NAMES = ["Poivre noir, poudre", "Sel blanc alimentaire"];
const INGREDIENT_SERVINGS_OPTIONS = [
  { value: 4, label: "Pour 4 personnes" },
  { value: 2, label: "Pour 2 personnes" },
];

function getIngredientName(ingredient) {
  return ingredient?.aliment?.display_name || ingredient?.aliment?.name || "";
}

function isHiddenIngredient(ingredient) {
  const ingredientName = getIngredientName(ingredient).trim().toLowerCase();

  return HIDDEN_INGREDIENT_NAMES.some((hiddenName) => ingredientName.startsWith(hiddenName.toLowerCase()));
}

function normalizeUnit(unit) {
  return String(unit || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function parseQuantityValue(value) {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  const normalizedValue = String(value || "").trim().replace(",", ".");
  if (!normalizedValue) {
    return null;
  }

  const parsedValue = Number.parseFloat(normalizedValue);
  return Number.isFinite(parsedValue) ? parsedValue : null;
}

function roundToStep(value, step) {
  return Math.round(value / step) * step;
}

function formatNumber(value, maxDecimals = 1) {
  const roundedValue = Number(value.toFixed(maxDecimals));

  if (Number.isInteger(roundedValue)) {
    return String(roundedValue);
  }

  return String(roundedValue).replace(".", ",");
}

function formatScaledQuantity(value, unit) {
  const normalizedUnit = normalizeUnit(unit);
  const countLikeUnits = new Set(["piece", "pieces", "pièce", "pièces", "pincee", "pincees", "pincée", "pincées"]);
  const weightVolumeUnits = new Set(["g", "gr", "gramme", "grammes", "ml", "cl", "kg", "l"]);

  if (countLikeUnits.has(normalizedUnit)) {
    return formatNumber(roundToStep(value, 0.5), 1);
  }

  if (weightVolumeUnits.has(normalizedUnit)) {
    return formatNumber(value, 1);
  }

  const roundedValue = value >= 10 ? value : roundToStep(value, 0.5);
  return formatNumber(roundedValue, 1);
}

export default {
  name: "PublicRecipeView",
  components: {
    LoaderView,
  },
  data() {
    return {
      loading: true,
      recipe: null,
      mostViewedRecipes: [],
      latestRecipes: [],
      mostGeneratedRecipes: [],
      similarRecipes: [],
      qrCodeDataUrl: "",
      actionMessage: "",
      actionMessageTimer: null,
      downloadingPdf: false,
      activeIngredientServings: 4,
      ingredientServingsOptions: INGREDIENT_SERVINGS_OPTIONS,
      logoSrc: require("@/assets/app/logo-version-allongee-baseline-couleur.png"),
    };
  },
  computed: {
    id() {
      return this.$route.params.id;
    },
    publicUrl() {
      if (typeof window === "undefined") {
        return `/recette/${this.id}`;
      }

      return window.location.href;
    },
    metaDescription() {
      if (!this.recipe) {
        return "Découvrez une recette Mes Meilleurs Menus.";
      }

      return this.shortRecipe(this.recipe.recipe, 180);
    },
    visibleDishLines() {
      if (!Array.isArray(this.recipe?.dish_lines)) {
        return [];
      }

      return this.recipe.dish_lines.filter((ingredient) => !isHiddenIngredient(ingredient));
    },
    highlightFlags() {
      if (!this.recipe) {
        return [];
      }

      const flags = [];

      if (this.recipe.cetogene) {
        flags.push("Cétogène");
      }
      if (this.recipe.meat) {
        flags.push("Contient de la viande");
      }
      if (this.recipe.fish) {
        flags.push("Contient du poisson");
      }

      return flags;
    },
    recipeInfoCards() {
      if (!this.recipe) {
        return [];
      }

      return [
        {
          label: "Niveau de difficulté",
          value: this.recipe.difficulty || "Non renseigné",
          icon: ["fas", "signal"],
        },
        {
          label: "Temps de préparation + cuisson",
          value: `${this.recipe.preparation_time || 0} min`,
          icon: ["fas", "clock"],
        },
        {
          label: "Coût",
          value: this.recipe.price || "Non renseigné",
          icon: ["fas", "wallet"],
        },
        {
          label: "Indice glycémique",
          value: this.humanizeGlycemicIndex(this.recipe.glycemic_index),
          icon: ["fas", "chart-line"],
        },
        {
          label: "Macro-Nutriments",
          value: this.humanizeMacroNutriments(this.recipe.macro_nutriments) || "Non renseigné",
          icon: ["fas", "layer-group"],
        },
        {
          label: "Végétarien",
          value: this.recipe.is_vegan ? "Oui" : "Non",
          icon: ["fas", "seedling"],
        },
        {
          label: "Sans gluten",
          value: this.recipe.is_gluten_free ? "Oui" : "Non",
          icon: ["fas", "ban"],
        },
      ];
    },
    facebookShareUrl() {
      return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(this.publicUrl)}`;
    },
    linkedinShareUrl() {
      return `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(this.publicUrl)}`;
    },
    relatedSections() {
      const sections = [
        {
          key: "most-viewed",
          title: "Les plus regardées",
          recipes: this.mostViewedRecipes.slice(0, 4),
        },
        {
          key: "latest",
          title: "Les plus récentes",
          recipes: this.latestRecipes.slice(0, 4),
        },
        {
          key: "most-generated",
          title: "Les plus proposées par notre générateur",
          recipes: this.mostGeneratedRecipes.slice(0, 4),
        },
        {
          key: "similar",
          title: "Recettes similaires",
          recipes: this.similarRecipes.slice(0, 4),
        },
      ];

      return sections.filter((section) => section.recipes.length > 0);
    },
  },
  watch: {
    id: {
      immediate: true,
      handler() {
        this.reload();
      },
    },
  },
  beforeDestroy() {
    if (this.actionMessageTimer) {
      clearTimeout(this.actionMessageTimer);
    }
  },
  methods: {
    async reload() {
      this.loading = true;

      try {
        const response = await api.get(`/recipes/${this.id}/`);
        const payload = response.data || {};

        this.recipe = payload.recipe || null;
        this.mostViewedRecipes = payload.most_viewed_recipes || [];
        this.latestRecipes = payload.latest_recipes || [];
        this.mostGeneratedRecipes = payload.most_generated_recipes || [];
        this.similarRecipes = payload.similar_recipes || [];

        await this.refreshQrCode();
        this.updateHead();
      } finally {
        this.loading = false;
      }
    },
    async refreshQrCode() {
      if (!this.recipe) {
        this.qrCodeDataUrl = "";
        return;
      }

      try {
        this.qrCodeDataUrl = await QRCode.toDataURL(this.publicUrl, {
          width: 160,
          margin: 1,
          color: {
            dark: "#004c40",
            light: "#ffffff",
          },
        });
      } catch (error) {
        this.qrCodeDataUrl = "";
      }
    },
    updateHead() {
      if (!this.recipe) return;

      const title = `${this.recipe.name} | Recette Mes Meilleurs Menus`;
      const description = this.metaDescription;
      const image = this.recipe.photo ? this.mediaSrc(this.recipe.photo) : `${window.location.origin}/default-recipe.jpg`;

      document.title = title;

      this.setMetaTag("description", description);
      this.setMetaTag("og:title", title, true);
      this.setMetaTag("og:description", description, true);
      this.setMetaTag("og:type", "article", true);
      this.setMetaTag("og:url", this.publicUrl, true);
      this.setMetaTag("og:image", image, true);
      this.setMetaTag("twitter:card", "summary_large_image");
      this.setMetaTag("twitter:title", title);
      this.setMetaTag("twitter:description", description);
      this.setMetaTag("twitter:image", image);
      this.setCanonicalLink(this.publicUrl);
      this.setJsonLd();
    },
    setMetaTag(name, content, isProperty = false) {
      const selector = isProperty ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let tag = document.head.querySelector(selector);

      if (!tag) {
        tag = document.createElement("meta");
        if (isProperty) {
          tag.setAttribute("property", name);
        } else {
          tag.setAttribute("name", name);
        }
        document.head.appendChild(tag);
      }

      tag.setAttribute("content", content);
    },
    setCanonicalLink(url) {
      let tag = document.head.querySelector('link[rel="canonical"]');

      if (!tag) {
        tag = document.createElement("link");
        tag.setAttribute("rel", "canonical");
        document.head.appendChild(tag);
      }

      tag.setAttribute("href", url);
    },
    setJsonLd() {
      const existing = document.head.querySelector("#recipe-json-ld");
      if (existing) {
        existing.remove();
      }

      if (!this.recipe) {
        return;
      }

      const jsonLd = {
        "@context": "https://schema.org",
        "@type": "Recipe",
        name: this.recipe.name,
        image: this.recipe.photo ? [this.mediaSrc(this.recipe.photo)] : [],
        description: this.metaDescription,
        recipeIngredient: this.visibleDishLines.map(
          (ingredient) => `${ingredient.quantity} ${ingredient.unit} ${this.ingredientName(ingredient)}`,
        ),
        recipeInstructions: this.recipe.recipe,
        prepTime: `PT${this.recipe.preparation_time}M`,
        recipeCategory: this.recipe.dish_type_display || this.recipe.dish_type,
      };

      const script = document.createElement("script");
      script.id = "recipe-json-ld";
      script.type = "application/ld+json";
      script.text = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    },
    mediaSrc(uri) {
      if (!uri) return null;
      if (uri.startsWith("http")) return uri;
      return `${process.env.VUE_APP_BASE_URL}${uri}`;
    },
    ingredientName(ingredient) {
      return getIngredientName(ingredient);
    },
    isHiddenIngredient(ingredient) {
      return isHiddenIngredient(ingredient);
    },
    formattedIngredientAmount(ingredient) {
      const baseQuantity = parseQuantityValue(ingredient?.quantity);
      const unit = String(ingredient?.unit || "").trim();

      if (baseQuantity === null) {
        return [ingredient?.quantity, unit].filter(Boolean).join(" ");
      }

      const scaledQuantity = baseQuantity * this.activeIngredientServings;
      return [formatScaledQuantity(scaledQuantity, unit), unit].filter(Boolean).join(" ");
    },
    shortRecipe(recipe, limit = 140) {
      const text = (recipe || "").replace(/\s+/g, " ").trim();

      if (!text) {
        return "Découvrez cette recette Mes Meilleurs Menus et consultez sa préparation détaillée.";
      }

      if (text.length <= limit) {
        return text;
      }

      return `${text.slice(0, limit - 3).trim()}...`;
    },
    humanizeGlycemicIndex(value) {
      if (value === "low") return "Faible";
      if (value === "medium") return "Moyen";
      if (value === "high") return "Haut";
      return "Non renseigné";
    },
    humanizeMacroNutriments(value) {
      if (!value) return "";
      const map = { P: "Protéines", L: "Lipides", G: "Glucides" };
      return value.split("-").map(l => map[l] || l).join(" - ");
    },
    async copyText(text) {
      if (typeof navigator !== "undefined" && navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        return;
      }

      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.setAttribute("readonly", "readonly");
      textarea.style.position = "absolute";
      textarea.style.left = "-9999px";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    },
    showActionMessage(message) {
      this.actionMessage = message;

      if (this.actionMessageTimer) {
        clearTimeout(this.actionMessageTimer);
      }

      this.actionMessageTimer = setTimeout(() => {
        this.actionMessage = "";
      }, 3200);
    },
    printRecipe() {
      window.print();
    },
    async downloadPdf() {
      if (!this.$refs.pdfSheet || this.downloadingPdf || !this.recipe) {
        return;
      }

      this.downloadingPdf = true;

      try {
        await this.$nextTick();

        const filename = `recette-${this.recipe.name
          .toLowerCase()
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-+|-+$/g, "") || this.id}.pdf`;

        await html2pdf()
          .set({
            margin: [0, 0, 0, 0],
            filename,
            image: { type: "jpeg", quality: 0.98 },
            html2canvas: {
              scale: 1.8,
              useCORS: true,
              backgroundColor: "#ffffff",
            },
            jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
            pagebreak: { mode: ["css", "legacy"] },
          })
          .from(this.$refs.pdfSheet)
          .save();
      } catch (error) {
        this.showActionMessage("Le téléchargement du PDF a échoué. Réessayez dans un instant.");
      } finally {
        this.downloadingPdf = false;
      }
    },
    async shareOnInstagram() {
      try {
        await this.copyText(this.publicUrl);
        this.showActionMessage("Le lien de la recette a été copié pour le partager sur Instagram.");
      } catch (error) {
        this.showActionMessage("Impossible de copier le lien automatiquement.");
      }
    },
  },
};
</script>

<style scoped>
.public-recipe-view {
  padding: 180px 20px 48px;
}

.public-recipe-view__inner {
  max-width: 1320px;
  margin: 0 auto;
}

.recipe-layout {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.recipe-hero {
  display: grid;
  grid-template-columns: minmax(320px, 480px) minmax(0, 1fr);
  gap: 28px;
  align-items: start;
}

.recipe-hero__media {
  border-radius: 22px;
  overflow: hidden;
  background: #eef3ea;
  min-height: 320px;
}

.recipe-hero__image {
  width: 100%;
  height: 100%;
  min-height: 320px;
  object-fit: cover;
  display: block;
}

.recipe-hero__fallback {
  min-height: 320px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: linear-gradient(135deg, rgba(181, 208, 136, 0.92), rgba(0, 76, 64, 0.92));
  color: #fff;
  font-size: 28px;
  font-weight: 700;
  text-align: center;
}

.recipe-hero__eyebrow {
  margin: 0 0 10px;
  color: #e57b57;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.recipe-hero__title {
  margin: 0;
  color: #004c40;
  font-family: "Amanise";
  font-size: clamp(34px, 4vw, 58px);
  line-height: 1.05;
}

.recipe-hero__intro {
  margin: 16px 0 0;
  color: #35524a;
  font-size: 17px;
  line-height: 1.7;
}

.recipe-hero__meta,
.recipe-hero__flags {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 18px;
}

.recipe-chip,
.recipe-flag {
  display: inline-flex;
  align-items: center;
  min-height: 36px;
  padding: 0 14px;
  border-radius: 999px;
  background: #eef3ea;
  color: #004c40;
  font-size: 14px;
  font-weight: 700;
}

.recipe-info {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  margin-top: 24px;
}

.recipe-info-card {
  display: flex;
  align-items: center;
  gap: 14px;
  min-height: 96px;
  padding: 16px 18px;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 12px 30px rgba(0, 76, 64, 0.08);
}

.recipe-info-card__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: linear-gradient(135deg, rgba(181, 208, 136, 0.24), rgba(0, 76, 64, 0.12));
  color: #004c40;
  font-size: 18px;
  flex: 0 0 auto;
}

.recipe-info-card__body {
  min-width: 0;
}

.recipe-info-card__label,
.recipe-info-card__value {
  margin: 0;
}

.recipe-info-card__label {
  color: #6b7f78;
  font-size: 13px;
  font-weight: 700;
}

.recipe-info-card__value {
  margin-top: 4px;
  color: #004c40;
  font-size: 17px;
  font-weight: 700;
  line-height: 1.3;
}

.recipe-tools {
  display: grid;
  grid-template-columns: 160px minmax(0, 1fr);
  gap: 18px;
  margin-top: 24px;
}

.recipe-qr {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 18px;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 12px 30px rgba(0, 76, 64, 0.08);
}

.recipe-qr__label {
  margin: 0;
  color: #35524a;
  font-size: 13px;
  font-weight: 700;
  text-align: center;
}

.recipe-qr__image,
.recipe-qr__placeholder {
  width: 118px;
  height: 118px;
}

.recipe-qr__image {
  display: block;
  border-radius: 12px;
  background: #fff;
}

.recipe-qr__placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background: #eef3ea;
  color: #004c40;
  font-size: 32px;
}

.recipe-actions {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 16px;
  padding: 18px 20px;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 12px 30px rgba(0, 76, 64, 0.08);
}

.recipe-action-group {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 16px;
}

.recipe-action-group__label {
  margin: 0;
  color: #35524a;
  font-size: 14px;
  font-weight: 700;
}

.recipe-action-group__buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.recipe-action-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  height: 46px;
  border: 0;
  border-radius: 999px;
  color: #fff;
  text-decoration: none;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;
}

.recipe-action-icon:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 22px rgba(0, 76, 64, 0.14);
}

.recipe-action-icon:disabled {
  opacity: 0.5;
  cursor: default;
  transform: none;
  box-shadow: none;
}

.recipe-action-icon--facebook {
  background: #1877f2;
}

.recipe-action-icon--linkedin {
  background: #0a66c2;
}

.recipe-action-icon--instagram {
  background: linear-gradient(135deg, #fdc468, #df4996 55%, #4f5bd5);
}

.recipe-action-icon--utility {
  background: #004c40;
}

.recipe-actions__feedback {
  margin: 0;
  color: #004c40;
  font-size: 14px;
  font-weight: 700;
}

.recipe-content__main {
  display: grid;
  grid-template-columns: 320px minmax(0, 1fr);
  gap: 24px;
}

.recipe-panel {
  padding: 24px;
  border-radius: 20px;
  background: #fff;
  box-shadow: 0 12px 30px rgba(0, 76, 64, 0.08);
}

.recipe-panel h2,
.related-section h2 {
  margin: 0 0 18px;
  color: #004c40;
  font-size: 28px;
}

.ingredients-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 18px;
}

.ingredients-tab {
  min-height: 30px;
  padding: 0 10px;
  border: 1px solid #d8ddd2;
  border-radius: 999px;
  background: #f5f1e8;
  color: #35524a;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
}

.ingredients-tab.is-active {
  border-color: #004c40;
  background: #004c40;
  color: #fff;
  box-shadow: 0 10px 22px rgba(0, 76, 64, 0.12);
}

.ingredients-list {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.ingredients-list li {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  color: #35524a;
}

.recipe-text {
  white-space: pre-line;
  color: #35524a;
  font-size: 16px;
  line-height: 1.85;
}

.recipe-promo-text {
  margin: 0;
  padding: 18px 22px;
  border-radius: 18px;
  background: #f5f1e8;
  color: #35524a;
  font-size: 15px;
  line-height: 1.7;
}

.related-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 20px;
  align-items: stretch;
}

.related-card {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 10px 24px rgba(0, 76, 64, 0.08);
  color: inherit;
  text-decoration: none;
}

.related-card__media {
  aspect-ratio: 4 / 3;
  background: #e8efe8;
}

.related-card__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.related-card__fallback {
  width: 100%;
  height: 100%;
  min-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
  background: linear-gradient(135deg, rgba(181, 208, 136, 0.92), rgba(0, 76, 64, 0.92));
  color: #fff;
  text-align: center;
  font-weight: 700;
}

.related-card__content {
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 18px;
}

.related-card__meta {
  margin: 0 0 8px;
  color: #e57b57;
  font-size: 13px;
  font-weight: 700;
}

.related-card__title {
  margin: 0 0 10px;
  color: #004c40;
  font-size: 20px;
  line-height: 1.3;
}

.related-card__excerpt {
  margin: 0;
  color: #4a5f58;
  font-size: 14px;
  line-height: 1.6;
}

.recipe-export-print {
  position: fixed;
  top: 0;
  left: -99999px;
  width: 210mm;
  background: #fff;
}

.recipe-export-sheet {
  width: 210mm;
  height: 296mm;
  padding: 6mm;
  box-sizing: border-box;
  overflow: hidden;
  break-after: avoid-page;
  page-break-after: avoid;
  color: #213732;
  background:
    radial-gradient(circle at top right, rgba(181, 208, 136, 0.18), transparent 32%),
    linear-gradient(180deg, #f8fbf7 0%, #ffffff 100%);
}

.recipe-export-sheet,
.recipe-export-sheet * {
  box-sizing: border-box;
}

.recipe-export-sheet__hero {
  padding: 5.5mm;
  border-radius: 6mm;
  background: linear-gradient(140deg, rgba(0, 76, 64, 0.96), rgba(0, 76, 64, 0.78));
  color: #fff;
  box-shadow: 0 2.5mm 7mm rgba(0, 76, 64, 0.14);
}

.recipe-export-sheet__brand {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4mm;
}

.recipe-export-sheet__logo {
  width: 48mm;
  height: auto;
  display: block;
}

.recipe-export-sheet__eyebrow {
  margin: 0;
  color: rgba(255, 255, 255, 0.82);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.recipe-export-sheet__hero-main {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 24mm;
  gap: 4mm;
  align-items: start;
  margin-top: 4mm;
}

.recipe-export-sheet__hero-copy {
  min-width: 0;
}

.recipe-export-sheet__type {
  display: inline-flex;
  align-items: center;
  min-height: 7mm;
  margin: 0 0 2.5mm;
  padding: 0 2.5mm;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
  font-size: 9px;
  font-weight: 700;
}

.recipe-export-sheet__title {
  margin: 0;
  color: #188143;
  font-family: "Amanise";
  font-size: 22px;
  line-height: 1.05;
}

.recipe-export-sheet__description {
  margin: 2.5mm 0 0;
  color: rgba(255, 255, 255, 0.9);
  font-size: 10px;
  line-height: 1.35;
}

.recipe-export-sheet__qr {
  display: flex;
  justify-content: flex-end;
  min-width: 0;
}

.recipe-export-sheet__qr-image {
  display: block;
  width: 100%;
  height: auto;
  padding: 1.2mm;
  border-radius: 3mm;
  background: #fff;
}

.recipe-export-sheet__summary {
  display: grid;
  grid-template-columns: 40mm minmax(0, 1fr);
  gap: 4mm;
  margin-top: 4.5mm;
  align-items: start;
  min-width: 0;
}

.recipe-export-sheet__photo-wrap {
  border-radius: 4mm;
  overflow: hidden;
  background: #eef3ea;
  box-shadow: 0 1.5mm 4mm rgba(0, 76, 64, 0.08);
}

.recipe-export-sheet__photo {
  width: 100%;
  height: 100%;
  min-height: 46mm;
  object-fit: cover;
  display: block;
}

.recipe-export-sheet__meta {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 2mm;
  min-width: 0;
}

.recipe-export-meta {
  display: flex;
  align-items: center;
  gap: 2.5mm;
  min-height: 15mm;
  min-width: 0;
  padding: 2.5mm;
  border: 0.4mm solid #dce5da;
  border-radius: 3mm;
  background: rgba(255, 255, 255, 0.96);
  break-inside: avoid;
  page-break-inside: avoid;
}

.recipe-export-meta__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 7.5mm;
  height: 7.5mm;
  border-radius: 2.4mm;
  background: linear-gradient(135deg, rgba(181, 208, 136, 0.25), rgba(0, 76, 64, 0.12));
  color: #004c40;
  font-size: 9px;
  flex: 0 0 auto;
}

.recipe-export-meta__label,
.recipe-export-meta__value {
  margin: 0;
}

.recipe-export-meta__label {
  color: #5f726b;
  font-size: 8px;
  font-weight: 700;
  line-height: 1.2;
  overflow-wrap: anywhere;
}

.recipe-export-meta__value {
  margin-top: 0.8mm;
  color: #004c40;
  font-size: 9px;
  font-weight: 700;
  line-height: 1.2;
  overflow-wrap: anywhere;
}

.recipe-export-sheet__columns {
  display: grid;
  grid-template-columns: 64mm minmax(0, 1fr);
  gap: 4mm;
  margin-top: 4.5mm;
  min-width: 0;
}

.recipe-export-panel {
  min-width: 0;
  padding: 3.5mm;
  border-radius: 4mm;
  background: rgba(255, 255, 255, 0.97);
  box-shadow: 0 1.5mm 4mm rgba(0, 76, 64, 0.08);
  break-inside: avoid;
  page-break-inside: avoid;
}

.recipe-export-panel h2 {
  margin: 0 0 2.5mm;
  color: #004c40;
  font-size: 13px;
}

.recipe-export-panel--preparation {
  background:
    linear-gradient(180deg, rgba(245, 241, 232, 0.62), rgba(255, 255, 255, 0.98));
}

.recipe-export-ingredients {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 1.6mm;
}

.recipe-export-ingredients li {
  display: flex;
  justify-content: space-between;
  gap: 2mm;
  color: #35524a;
  font-size: 9.4px;
  line-height: 1.22;
}

.recipe-export-ingredients li span {
  min-width: 0;
  overflow-wrap: anywhere;
}

.recipe-export-ingredients li strong {
  flex: 0 0 auto;
}

.recipe-export-text {
  white-space: pre-line;
  color: #35524a;
  font-size: 9.2px;
  line-height: 1.26;
  overflow-wrap: anywhere;
}

.recipe-export-sheet__footer {
  margin: 4mm 0 0;
  padding-top: 3mm;
  border-top: 0.4mm solid #dce5da;
  color: #35524a;
  font-size: 8px;
  line-height: 1.3;
}

@media only screen and (max-width: 1199px) {
  .recipe-hero {
    grid-template-columns: minmax(280px, 400px) minmax(0, 1fr);
  }

  .recipe-info {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media only screen and (max-width: 1023px) {

  .recipe-hero,
  .recipe-content__main,
  .recipe-tools {
    grid-template-columns: 1fr;
  }

  .related-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .recipe-qr {
    max-width: 220px;
  }
}

@media only screen and (max-width: 639px) {
  .public-recipe-view {
    padding: 132px 16px 40px;
  }

  .recipe-info,
  .related-grid {
    grid-template-columns: 1fr;
  }

  .recipe-actions {
    padding: 16px;
  }

  .recipe-action-group {
    align-items: flex-start;
    flex-direction: column;
  }

  .recipe-panel {
    padding: 20px;
  }
}

@media print {
  @page {
    size: A4 portrait;
    margin: 0;
  }

  .public-recipe-view {
    padding: 0 !important;
    margin: 0 !important;
    background: #fff !important;
  }

  .public-recipe-view__inner {
    display: none !important;
  }

  .recipe-export-print {
    position: fixed !important;
    inset: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
    overflow: hidden !important;
  }

  .recipe-export-sheet {
    margin: 0 !important;
  }
}
</style>

<style>
@media print {
  @page {
    size: A4 portrait;
    margin: 0;
  }

  html,
  body {
    margin: 0 !important;
    padding: 0 !important;
    width: 210mm !important;
    height: 297mm !important;
    overflow: hidden !important;
    background: #fff !important;
  }

  body * {
    visibility: hidden !important;
  }

  .public-recipe-view,
  .public-recipe-view .recipe-export-print,
  .public-recipe-view .recipe-export-print * {
    visibility: visible !important;
  }

  .public-recipe-view {
    position: static !important;
    width: 210mm !important;
    height: 297mm !important;
    min-height: 0 !important;
    overflow: hidden !important;
  }

  .public-recipe-view .recipe-export-print {
    position: fixed !important;
    inset: 0 !important;
    left: 0 !important;
    top: 0 !important;
    width: 210mm !important;
    height: 297mm !important;
    margin: 0 !important;
    padding: 0 !important;
    overflow: hidden !important;
    break-before: avoid-page !important;
    break-after: avoid-page !important;
    page-break-before: avoid !important;
    page-break-after: avoid !important;
  }

  .public-recipe-view .recipe-export-sheet {
    margin: 0 !important;
    break-before: avoid-page !important;
    break-after: avoid-page !important;
    page-break-before: avoid !important;
    page-break-after: avoid !important;
  }
}
</style>
